import {
  Directive,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  inject,
  HostAttributeToken,
  input,
} from '@angular/core';
import { fromEvent, Subscription, throttleTime } from 'rxjs';

/**
 * @description für Anzeige/Ausblendung abhängig von window;
 * @hides Element, wenn mit `*` benutzt;
 * @returns true/false durch template variable ('query' exposes directive) q.matches
 * @example *w-media-query="{min: 1000, max: 1200}"
 * @example [w-media-query]="{max: 600}" #q="query" [clrInMenu]="q.matches"
 */
@Directive({
  selector: '[w-media-query]',
  exportAs: 'query',
})
export class WindowMediaQueryDirective implements OnInit, OnDestroy {
  private readonly viewContainerRef = inject(ViewContainerRef, {
    optional: true,
  });
  private readonly templateRef = inject<TemplateRef<unknown>>(TemplateRef, {
    optional: true,
  });
  private readonly operator = inject(new HostAttributeToken('or_operator'), {
    optional: true,
  });
  private readonly dimension = inject(new HostAttributeToken('media_height'), {
    optional: true,
  });

  readonly mode = input.required<{
    min?: number;
    max?: number;
  }>({ alias: 'w-media-query' });

  private mediaQueryList: MediaQueryList;
  private sub: Subscription;
  public matches: boolean = false;

  constructor() {
    const operator = this.operator;
    const dimension = this.dimension;

    this.operator = operator !== null ? 'or' : 'and';
    this.dimension = dimension !== null ? 'height' : 'width';
  }

  ngOnInit() {
    const mode = this.mode();
    if (!mode || mode.min <= 0 || mode.max <= 0) {
      this.updateView(true);
      return;
    }

    const mediaQuery = Object.entries(mode).map(([key, px]) => `(${key}-${this.dimension}: ${px}px)`);

    this.mediaQueryList = matchMedia(mediaQuery.join(this.operator));
    this.updateView(this.mediaQueryList.matches);

    this.sub = fromEvent<MediaQueryListEvent>(this.mediaQueryList, 'change')
      .pipe(throttleTime(400))
      .subscribe(({ matches }) => this.updateView(matches));
  }

  private updateView = (matches: boolean) => {
    this.matches = matches;
    if (matches && this.templateRef) {
      const ref = this.viewContainerRef?.createEmbeddedView(this.templateRef);
      ref?.markForCheck();
    } else this.viewContainerRef?.clear();
  };

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
